// The Share button on folders, lists, spaces, tasks and docs: copy the link and, for members,
// share the item with guests and choose what they can do with it. Sharing a folder or list
// includes everything inside it; a task includes its subtasks. Guests can't be added to spaces.
import { useMemo, useState } from 'react';
import type { SharePermission, Shares, User } from '@shared/types';
import { atLeast, roleOf, SHARE_PERMISSIONS } from '@shared/roles';
import { ChevronDown, Link2, Share2, UserMinus, UserTag } from '@/components/ui/icons';
import { useDb, useMe, type Tables } from '@/lib/store';
import { useWorkspaceId } from '@/lib/nav';
import { setShare, type ShareTarget } from '@/lib/actions';
import { SHARE_LEVELS } from '@/lib/roles';
import { Avatar } from '@/components/ui/Avatar';
import { Popover } from '@/components/ui/Popover';
import { MenuItem, MenuSeparator } from '@/components/ui/Menu';
import { OptionMenu } from '@/components/ui/OptionMenu';
import { toast } from '@/components/ui/overlays';
import { WorkspaceLogo } from '@/shell/WorkspaceLogo';
import { InviteModal } from '@/shell/InviteModal';

export type Shareable = ShareTarget | { type: 'space' | 'workspace'; id: string };

const NOUN: Record<Shareable['type'], string> = { folder: 'Folder', list: 'List', task: 'Task', doc: 'Doc', space: 'Space', workspace: 'view' };

const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

function itemOf(db: Tables, t: Shareable): { shares?: Shares } | undefined {
  if (t.type === 'folder') return db.folders[t.id];
  if (t.type === 'list') return db.lists[t.id];
  if (t.type === 'task') return db.tasks[t.id];
  if (t.type === 'doc') return db.docs[t.id];
  return undefined;
}

/** Shares given further up: a task's parent tasks, list and folder; a list's folder; a doc's list or folder. */
function sharedAbove(db: Tables, t: Shareable): { shares?: Shares; via: string }[] {
  const out: { shares?: Shares; via: string }[] = [];
  const folder = (id: string) => db.folders[id] && out.push({ shares: db.folders[id].shares, via: db.folders[id].name });
  const list = (id: string) => {
    const l = db.lists[id];
    if (!l) return;
    out.push({ shares: l.shares, via: l.name });
    if (l.folderId) folder(l.folderId);
  };
  if (t.type === 'task') {
    const seen = new Set<string>();
    for (let p = db.tasks[t.id]?.parentId; p && db.tasks[p] && !seen.has(p); p = db.tasks[p].parentId) {
      seen.add(p);
      out.push({ shares: db.tasks[p].shares, via: db.tasks[p].name });
    }
    if (db.tasks[t.id]) list(db.tasks[t.id].listId);
  } else if (t.type === 'list') {
    const f = db.lists[t.id]?.folderId;
    if (f) folder(f);
  } else if (t.type === 'doc') {
    const loc = db.docs[t.id]?.location;
    if (loc?.type === 'list') list(loc.id);
    else if (loc?.type === 'folder') folder(loc.id);
  }
  return out;
}

export function ShareButton({ target, link, className = 'btn', iconSize = 14 }: { target: Shareable; link: () => string; className?: string; iconSize?: number }) {
  const [invite, setInvite] = useState<string | null>(null);
  return (
    <>
      <Popover placement="bottom-end" content={(close) => <SharePanel target={target} link={link} onInvite={(email) => (close(), setInvite(email))} onDone={close} />}>
        <button className={className}>
          <Share2 size={iconSize} /> Share
        </button>
      </Popover>
      <InviteModal open={invite !== null} onClose={() => setInvite(null)} email={invite ?? ''} role="guest" />
    </>
  );
}

function SharePanel({ target, link, onInvite, onDone }: { target: Shareable; link: () => string; onInvite: (email: string) => void; onDone: () => void }) {
  const ws = useWorkspaceId();
  const me = useMe();
  const db = useDb((d) => d);
  const workspace = db.workspaces[ws];
  const [q, setQ] = useState('');
  const myRole = roleOf(workspace, me);
  const shareable = target.type !== 'space' && target.type !== 'workspace';
  const canShare = shareable && myRole !== 'guest';
  const shares = itemOf(db, target)?.shares ?? {};

  const guests = useMemo(
    () => (workspace?.memberIds ?? []).filter((id) => workspace?.roles?.[id] === 'guest').map((id) => db.users[id]).filter((u): u is User => !!u),
    [workspace, db.users],
  );
  // guests who reach this item through something above it (their best share and where it's from)
  const above = useMemo(() => {
    const out = new Map<string, { level: SharePermission; via: string }>();
    for (const { shares: s, via } of sharedAbove(db, target)) {
      for (const [u, level] of Object.entries(s ?? {})) {
        const cur = out.get(u);
        if (!cur || !atLeast(cur.level, level)) out.set(u, { level, via });
      }
    }
    return out;
  }, [db, target]);

  const text = q.trim().toLowerCase();
  const matches = text ? guests.filter((u) => !shares[u.id] && (u.name.toLowerCase().includes(text) || u.email.toLowerCase().includes(text))) : [];
  const known = Object.values(db.users).find((u) => u.email.toLowerCase() === text);
  const inviteEmail = isEmail(q) && !known ? q.trim() : null;
  const direct = Object.entries(shares)
    .map(([id, level]) => ({ user: db.users[id], level }))
    .filter((r): r is { user: User; level: SharePermission } => !!r.user && workspace?.roles?.[r.user.id] === 'guest')
    .sort((a, b) => a.user.name.localeCompare(b.user.name));
  const inherited = [...above].filter(([id]) => !shares[id] && db.users[id] && workspace?.roles?.[id] === 'guest');

  const add = (u: User) => {
    setShare(target as ShareTarget, u.id, 'edit');
    setQ('');
    toast(`Shared with ${u.name}`);
  };
  const copy = () => {
    void navigator.clipboard.writeText(link());
    toast('Link copied to clipboard');
    onDone();
  };

  return (
    <div className="share-panel">
      <div className="share-title">Share this {NOUN[target.type]}</div>
      {canShare && (
        <div className="share-invite">
          <input
            className="input"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              if (matches.length === 1) add(matches[0]);
              else if (inviteEmail) onInvite(inviteEmail);
            }}
            placeholder="Invite a guest by name or email"
            aria-label="Invite a guest by name or email"
          />
          {text && (
            <div className="share-suggest">
              {matches.map((u) => (
                <MenuItem key={u.id} icon={<Avatar userId={u.id} size={20} />} label={u.name} hint={u.email} onClick={() => add(u)} />
              ))}
              {inviteEmail && <MenuItem icon={<UserTag size={15} />} label={`Invite ${inviteEmail} as a guest`} onClick={() => onInvite(inviteEmail)} />}
              {!matches.length && !inviteEmail && (
                <div className="share-empty">
                  {known && roleOf(workspace, known.id) !== 'guest' ? `${known.name} is a ${roleOf(workspace, known.id)} and already sees everything.` : 'No guests match. Type an email address to invite one.'}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {target.type === 'space' && <p className="share-note">Guests can’t be added to Spaces. Share a folder, list or task inside it with them instead.</p>}

      <div className="share-label">Who has access</div>
      <div className="share-row">
        <WorkspaceLogo workspaceId={ws} size={28} />
        <span className="share-who">
          <span className="truncate">Everyone at {workspace?.name ?? 'this workspace'}</span>
          <span className="share-sub">Members and admins</span>
        </span>
        <span className="share-level static">Full edit</span>
      </div>
      {direct.map(({ user, level }) => (
        <div key={user.id} className="share-row">
          <Avatar userId={user.id} size={28} showPresence />
          <span className="share-who">
            <span className="truncate">{user.name}</span>
            <span className="share-sub truncate">Guest · {user.email}</span>
          </span>
          {canShare ? (
            <Popover
              placement="bottom-end"
              content={(close) => (
                <OptionMenu options={SHARE_PERMISSIONS.map((key) => ({ key, ...SHARE_LEVELS[key] }))} value={level} onPick={(next) => (setShare(target as ShareTarget, user.id, next), close())}>
                  <MenuSeparator />
                  <MenuItem icon={<UserMinus size={15} />} label="Remove access" danger onClick={() => (setShare(target as ShareTarget, user.id, null), close(), toast(`${user.name} no longer has access`))} />
                </OptionMenu>
              )}
            >
              <button className="share-level" aria-label={`What ${user.name} can do`}>
                {SHARE_LEVELS[level].label} <ChevronDown size={12} />
              </button>
            </Popover>
          ) : (
            <span className="share-level static">{SHARE_LEVELS[level].label}</span>
          )}
        </div>
      ))}
      {inherited.map(([id, { level, via }]) => (
        <div key={id} className="share-row">
          <Avatar userId={id} size={28} showPresence />
          <span className="share-who">
            <span className="truncate">{db.users[id].name}</span>
            <span className="share-sub truncate">Guest · from {via}</span>
          </span>
          <span className="share-level static">{SHARE_LEVELS[level].label}</span>
        </div>
      ))}

      <button className="share-copy" onClick={copy}>
        <Link2 size={15} /> Copy link
      </button>
    </div>
  );
}
